// [US 6.9] Client alertes Sentinel — préférences d'alerte (fig. 10) et, à venir, dashboard du soir.
// Toutes les requêtes passent par authClient.fetch (Bearer + retry refresh transparent).
import { authClient } from './authClient';

export type AlertChannel = 'push' | 'email' | 'both';

// Miroir de DEFAULT_ALERT_PREFS côté serveur (sentinelAlertsService.js).
export interface AlertPrefs {
    enabled: boolean;          // « Alerte GO quotidienne »
    includeMarginal: boolean;  // « Inclure les nuits MARGINAL »
    includeNoGo: boolean;      // opt-in NO-GO (jamais de NO-GO sauf activation explicite)
    minHours: number;          // seuil d'heures exploitables (0,5–12)
    sendHour: number;          // heure d'envoi locale (0-23)
    quietDays: number[];       // jours silencieux, ISO 1-7 (1=lundi..7=dimanche)
    weeklyDigest: boolean;     // « Digest hebdo 7 nuits »
    channel: AlertChannel;
}

// ----- [US 6.9 inc.2] Rapport du soir (pull gratuit) — miroir de sentinelTonightService côté serveur.
export type SentinelDecision = 'GO' | 'MARGINAL' | 'NOGO';

// [US 6.24] Condition de la nuit : un fait COMMUN à tous les filtres (ciel, Lune, qualité de nuit),
// énoncé une seule fois. Code + valeurs chiffrées, jamais du texte — le rendu i18n appartient au
// client (`conditionLines` dans sentinelDecision.ts), sinon le multilingue se duplique.
export interface NightCondition {
    code: string;
    params: Record<string, string | number | null>;
}

// Ce qu'une condition a COÛTÉ à un filtre donné : la seule chose qui varie d'un filtre à l'autre.
export interface FilterImpact {
    cause: string;   // 'clouds' | 'moon_bright' | 'moon_dist' | 'weather_unavailable'
    minutes: number; // créneaux perdus, comptés indépendamment des autres causes
}

// Couverture nuageuse moyenne par couche (%) sur les créneaux visibles. Indépendante du filtre :
// c'est elle qui explique qu'un même ciel soit tolérable en narrowband et bloquant en large bande.
export interface CloudLayers {
    low: number;
    mid: number;
    high: number;
    highMax: number;
}

export interface FilterEval {
    decision: SentinelDecision;
    exploitableHours: number;
    exploitableMinutes: number;
    limitingFactors: string[];
    // Absent sur les évals antérieures à l'US 6.24 (compat cache nightly_evals) → toujours tester.
    impacts?: FilterImpact[];
    window?: { start: string; end: string };
}

export interface TonightProject {
    // BIGINT pg sérialisé en chaîne selon le driver → normaliser (String()) avant toute comparaison d'id.
    projectId: number | string;
    targetRef: string;
    decision: SentinelDecision;
    exploitableHours: number;
    exploitableMinutes: number;
    limitingFactors: string[];
    bestFilter?: string;
    remainingGoal: Record<string, number>;
    window?: { start: string; end: string };
    cloudCoverAvg?: number;
    cloudLayers?: CloudLayers;
    moonDistDeg?: number;
    // Faits communs de la nuit, en tête de l'explication (le ciel est le même pour tous les filtres).
    conditions?: NightCondition[];
    filters: Record<string, FilterEval>;
}

// `available:false` → reason ('no_projects' | 'no_site' | 'no_eval'). Sinon rapport daté.
export type TonightReport =
    | { available: false; reason: 'no_projects' | 'no_site' | 'no_eval' }
    | {
          available: true;
          isInProgress: boolean; // nuit en cours (fallback veille) → à dater explicitement
          date: string;          // local_date de l'éval (YYYY-MM-DD = soir de la nuit)
          decision: SentinelDecision;
          // darknessLevel absent sur les évals antérieures au fallback nautique (compat cache).
          night: { duskAstro: string | null; dawnAstro: string | null; darknessLevel?: 'astronomical' | 'nautical' | 'none' };
          moon: { illum: number | null };
          weatherSource: string | null;
          degraded: boolean;
          projects: TonightProject[];
          // [US 6.24] Recheck tardif (US 6.23) : verdict d'origine + heure de révision. `null` si
          // la nuit n'a pas été revue. Le reste du rapport reflète DÉJÀ le verdict corrigé.
          recheck?: { from: SentinelDecision; at: string | null } | null;
      };

// ----- [US 6.10] Calendrier 14 nuits (premium) — miroir de sentinelCalendarService côté serveur.
export interface CalendarNight {
    offsetDay: number;          // 0..13
    date: string;               // YYYY-MM-DD (soir de la nuit)
    astroOnly: boolean;         // true au-delà de l'horizon météo (J+7..J+13)
    moonIllum: number;          // 0..1 (fraction éclairée)
    moonPhase: number;          // 0..1 (0=nouvelle, .25=1er quartier, .5=pleine, .75=dernier quartier)
    decision: SentinelDecision; // agrégat tous projets de la nuit
}

export interface CalendarCell {
    decision: SentinelDecision;
    exploitableHours: number;
    bestFilter: string | null;
    astroOnly: boolean;
    limitingFactors: string[];
    // Résumé par filtre (distinction GO/NO-GO par filtre, ex. L NO-GO / Ha GO sous lune) + facteurs limitants.
    filters: Record<string, { decision: SentinelDecision; exploitableHours: number; limitingFactors: string[]; impacts?: FilterImpact[] }>;
    cloudLayers?: CloudLayers;
    conditions?: NightCondition[];
    window?: { start: string; end: string };
}

export interface CalendarRow {
    projectId: number | string; // BIGINT pg → normaliser (String()) avant comparaison
    targetRef: string;
    cells: CalendarCell[];      // 14 cellules alignées sur nights[]
}

export type CalendarReport =
    | { available: false; reason: 'not_generated' | 'premium_required' }
    | {
          available: true;
          refDate: string;        // nuit J
          generatedAt: string;    // ISO — « généré le … »
          days: number;           // 14
          weatherDays: number;    // 7
          weatherSource: string | null; // provenance météo (transparence) : 'openmeteo' | 'metno' | '7timer' (historique) | …
          degraded: boolean;      // météo servie par le provider de secours / cache périmé
          nights: CalendarNight[];
          projects: CalendarRow[];
          nextBigWindow: { offsetDay: number; date: string } | null;
      };

export const sentinelClient = {
    async getCalendar(): Promise<CalendarReport | null> {
        const res = await authClient.fetch('/api/sentinel/calendar');
        // [US 6.13] Gating serveur : 403 → état « premium requis » (UI cosmétique dédiée).
        if (res.status === 403) return { available: false, reason: 'premium_required' };
        if (!res.ok) return null;
        return (await res.json()) as CalendarReport;
    },

    async getTonight(): Promise<TonightReport | null> {
        const res = await authClient.fetch('/api/sentinel/tonight');
        if (!res.ok) return null;
        return (await res.json()) as TonightReport;
    },

    async getAlertPrefs(): Promise<AlertPrefs | null> {
        const res = await authClient.fetch('/api/sentinel/alert-prefs');
        if (!res.ok) return null;
        return (await res.json()).prefs as AlertPrefs;
    },

    // PUT partiel : le serveur fusionne avec l'existant et renvoie la structure complète normalisée.
    async setAlertPrefs(patch: Partial<AlertPrefs>): Promise<AlertPrefs | null> {
        const res = await authClient.fetch('/api/sentinel/alert-prefs', {
            method: 'PUT',
            body: JSON.stringify(patch),
        });
        if (!res.ok) return null;
        return (await res.json()).prefs as AlertPrefs;
    },

    // [phase B] Enregistre le token push FCM du device pour le compte (anti-partage : token unique serveur).
    async registerPushDevice(token: string, platform: 'web' | 'android', locale?: string): Promise<boolean> {
        const res = await authClient.fetch('/api/sentinel/push/devices', {
            method: 'POST',
            body: JSON.stringify({ token, platform, locale }),
        });
        return res.ok;
    },

    // [phase B] Désenregistre un token (refus de notifications).
    async unregisterPushDevice(token: string): Promise<boolean> {
        const res = await authClient.fetch('/api/sentinel/push/devices', {
            method: 'DELETE',
            body: JSON.stringify({ token }),
        });
        return res.ok;
    },
};
